/**
 * Type definition for translation function (compatible with i18next)
 */
export type TranslateFunction = (key: string) => string;

/**
 * Utility class for handling and formatting error messages from various sources
 */
export class ErrorUtil {
    /**
     * Gets a user-friendly message for common HTTP status codes
     *
     * @param statusCode - The HTTP status code
     * @param t - Optional translation function for i18n support
     * @returns A user-friendly message for the status code, or null if not a recognized status
     */
    private static getMessageForStatusCode(statusCode: number, t?: TranslateFunction): string | null {
        // List of supported status codes
        const supportedCodes = [
            400, 401, 402, 403, 404, 405, 406, 408, 409, 410,
            413, 414, 415, 422, 429,
            500, 501, 502, 503, 504, 505
        ];

        // Check if status code is supported
        if (!supportedCodes.includes(statusCode)) {
            return null;
        }

        // If translation function is provided, use it
        if (t) {
            return t(`globals.errors.${statusCode}`);
        }

        // Fallback to English messages if no translation function
        const statusMessages: Record<number, string> = {
            400: 'Bad request',
            401: 'Invalid email or password',
            402: 'Payment required',
            403: 'Access forbidden',
            404: 'Resource not found',
            405: 'Method not allowed',
            406: 'Not acceptable',
            408: 'Request timeout',
            409: 'Conflict',
            410: 'Resource no longer available',
            413: 'Request too large',
            414: 'URL too long',
            415: 'Unsupported media type',
            422: 'Validation failed',
            429: 'Too many requests. Please try again later',
            500: 'Internal server error',
            501: 'Not implemented',
            502: 'Bad gateway',
            503: 'Service unavailable',
            504: 'Gateway timeout',
            505: 'HTTP version not supported'
        };

        return statusMessages[statusCode] || null;
    }

    /**
     * Extracts a user-friendly error message from various error formats
     *
     * Handles multiple error formats:
     * - String errors: Returns the string directly
     * - Error objects with message property: Returns error.message
     * - API response errors with nested error property: Returns error.error
     * - HTTP status codes: Returns user-friendly message based on status code (with i18n support)
     * - Fallback: Returns default message
     *
     * @param error - The error object or string to extract a message from
     * @param defaultMessage - Optional default message if no message can be extracted
     * @param t - Optional translation function for i18n support
     * @returns A user-friendly error message string
     *
     * @example
     * // API error format: {message: "Invalid credentials", error: "Unauthorized", statusCode: 401}
     * const msg = ErrorUtil.extractErrorMessage(apiError); // Returns "Invalid credentials"
     *
     * @example
     * // API error with only status code: {statusCode: 401}
     * const msg = ErrorUtil.extractErrorMessage(apiError, undefined, t); // Returns translated "Invalid email or password"
     *
     * @example
     * // JavaScript Error object
     * const msg = ErrorUtil.extractErrorMessage(new Error("Something went wrong")); // Returns "Something went wrong"
     *
     * @example
     * // String error
     * const msg = ErrorUtil.extractErrorMessage("Connection failed"); // Returns "Connection failed"
     *
     * @example
     * // With translation function
     * const msg = ErrorUtil.extractErrorMessage(error, undefined, t); // Uses i18n translations
     */
    static extractErrorMessage(error: any, defaultMessage?: string, t?: TranslateFunction): string {
        // Use translated default message if translation function is provided
        const fallbackMessage = t ? t('globals.errors.default') : (defaultMessage || 'An error occurred');
        // Handle string errors
        if (typeof error === 'string') {
            return error;
        }

        // Handle null or undefined
        if (!error) {
            return fallbackMessage;
        }

        // Try to extract message from error object
        // Check for 'message' property first (most common)
        if (error.message && typeof error.message === 'string') {
            return error.message;
        }

        // Check for 'error' property (common in API responses)
        if (error.error && typeof error.error === 'string') {
            return error.error;
        }

        // Check for 'detail' property (some APIs use this)
        if (error.detail && typeof error.detail === 'string') {
            return error.detail;
        }

        // If error has a statusText (axios errors)
        if (error.statusText && typeof error.statusText === 'string') {
            return error.statusText;
        }

        // Check for HTTP status code and provide user-friendly message
        if (error.statusCode && typeof error.statusCode === 'number') {
            const statusMessage = this.getMessageForStatusCode(error.statusCode, t);
            if (statusMessage) {
                return statusMessage;
            }
        }

        // Also check for 'status' property (some libraries use this instead of statusCode)
        if (error.status && typeof error.status === 'number') {
            const statusMessage = this.getMessageForStatusCode(error.status, t);
            if (statusMessage) {
                return statusMessage;
            }
        }

        // Fallback to default message
        return fallbackMessage;
    }

    /**
     * Checks if an error is a network error
     *
     * @param error - The error object to check
     * @returns true if the error is a network error
     *
     * @example
     * if (ErrorUtil.isNetworkError(error)) {
     *   console.log("Network connection failed");
     * }
     */
    static isNetworkError(error: any): boolean {
        if (!error) {
            return false;
        }

        // Check for network/timeout error codes
        if (error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED' ||
            error.code === 'ETIMEDOUT' ||
            error.code === 'ERR_INTERNET_DISCONNECTED' ||
            error.code === 'ERR_CONNECTION_REFUSED') {
            return true;
        }

        // Check for network error in message
        if (error.message && typeof error.message === 'string') {
            return error.message.includes('Network Error') ||
                   error.message.includes('ERR_NETWORK') ||
                   error.message.includes('timeout');
        }

        return false;
    }

    /**
     * Formats an error for logging purposes
     *
     * @param error - The error to format
     * @param context - Optional context string to include in the log
     * @returns Formatted error string
     *
     * @example
     * console.error(ErrorUtil.formatErrorForLogging(error, "Login failed"));
     */
    static formatErrorForLogging(error: any, context?: string): string {
        const prefix = context ? `[${context}] ` : '';

        if (error instanceof Error) {
            return `${prefix}${error.name}: ${error.message}\n${error.stack || ''}`;
        }

        if (typeof error === 'object') {
            try {
                return `${prefix}${JSON.stringify(error, null, 2)}`;
            } catch {
                return `${prefix}[Complex Error Object]`;
            }
        }

        return `${prefix}${String(error)}`;
    }
}
